'use client'

import { Link } from '@/components/ui/deprecated-link'
import { Table } from '@/components/ui/table'
import { useCustomPush } from '@/lib/client/navigation'
import type {
  PaginatedMeta,
  SalaryType,
  SurveySubmissionType,
} from '@/lib/generated/graphql-types'
import { cn } from '@/lib/css-utils'
import { isHourly, isSalaryType } from '@/modules/salary/common/utils'
import { InviteSalariesBanner } from '@/modules/salary/table/invite-salaries-banner'
import { SalaryPagination } from '@/modules/salary/table/pagination'
import { MainRoutes } from '@/utils/routes'
import { useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import { useSurveySubmissionStatus } from '@/modules/onboarding/use-survey-submission-status'
import {
  SALARY_LOCUM_TENENS_VALUE,
  SALARY_PRN_VALUE,
} from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import { SalaryFilterParam } from '../constants'
import {
  type SalaryPayPeriod,
  SEE_MORE_ROW_HINT,
} from '@/modules/salary/table/constants'
import {
  SalaryForceLoginType,
  useSalaryTableStore,
} from '@/modules/salary/salary-table-context'
import cookies from 'js-cookie'
import { useSignupTracking } from '@/modules/salary/analytics/use-signup-tracking'
import { useSalaryParams } from '../common/use-salary-params'
import { useSalaryFilters } from '../filters/use-salary-filters'
import { SalaryTableHeader } from './table-header'
import { SalaryTableMobile } from './table-mobile'
import { SalaryTableBody } from './table-body'

export function SalaryTable({
  salaries,
  salaryMeta,
  hasReachedLimit,
  className,
  isAuthenticated,
  showTableRowHint,
  countPublicInteraction,
}: {
  salaries: SalaryType[]
  salaryMeta?: PaginatedMeta
  hasReachedLimit: boolean
  className?: string
  isAuthenticated: boolean
  showTableRowHint?: boolean
  countPublicInteraction: () => void
}) {
  const { jobType } = useSalaryParams()
  const { pagesCount, pageNumber } = salaryMeta ?? {}
  const searchParams = useSearchParams()
  const push = useCustomPush()
  const { resetLoadingResults } = useSalaryFilters({
    isAuthenticated,
    countPublicInteraction,
  })
  const { isOnboardingComplete } = useSurveySubmissionStatus({
    isAuthenticated,
  })
  const setHasInteractedWithRow = useSalaryTableStore(
    (state) => state.setHasInteractedWithRow,
  )
  const setSalaryForceLoginType = useSalaryTableStore(
    (state) => state.setSalaryForceLoginType,
  )
  const { trackSignupClick } = useSignupTracking()

  const isSinglePage = pagesCount === 1
  const salaryIdParam = searchParams.get(SalaryFilterParam.salaryId)
  const isShowingSalaryDetails =
    salaryIdParam && salaries.some((s) => s.salaryId === salaryIdParam)

  const handleRowClick = (salary: SalaryType | SurveySubmissionType) => {
    if (!isSalaryType(salary)) return
    setHasInteractedWithRow(true)
    cookies.set(SEE_MORE_ROW_HINT, 'true')

    countPublicInteraction()
    push({ salaryId: salary.salaryId })
  }

  const onPageChange = (page: number) => {
    if (isAuthenticated || page === 1) return true
    setSalaryForceLoginType(SalaryForceLoginType.Pagination)
    return false
  }

  // use search params directly since filters are only set when calling getSalaryFilters
  const payPeriod = searchParams.get(SalaryFilterParam.payPeriod)

  const isHourlyPayPeriod = isHourly(
    jobType,
    payPeriod as SalaryPayPeriod | null,
  )

  const isLocumsOrPrn =
    jobType === SALARY_PRN_VALUE || jobType === SALARY_LOCUM_TENENS_VALUE

  useEffect(() => {
    // when the component mounts, set the loadingResults to null.
    // this happens every time the salary filters are updated.
    resetLoadingResults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaries])

  return (
    <div
      className={cn(
        'w-full mx-auto bg-background-page rounded-xl pb-6',
        className,
      )}
    >
      <div className="hidden md:block">
        <Table className="bg-background-primary">
          <SalaryTableHeader
            isShowingSalaryDetails={Boolean(isShowingSalaryDetails)}
            isHourlyPayPeriod={isHourlyPayPeriod}
            isLocumsOrPrn={isLocumsOrPrn}
          />
          <SalaryTableBody
            salaries={salaries}
            isShowingSalaryDetails={Boolean(isShowingSalaryDetails)}
            onRowClick={handleRowClick}
            blurTotalCompensation={hasReachedLimit}
            showTableRowHint={showTableRowHint}
            isHourlyPayPeriod={isHourlyPayPeriod}
            isLocumsOrPrn={isLocumsOrPrn}
          />
        </Table>
        {salaryMeta && !isSinglePage ? (
          <div
            className={cn(
              'flex justify-between items-end px-4 mt-6 gap-14',
              isOnboardingComplete ? 'justify-center' : null,
            )}
          >
            {!isOnboardingComplete ? (
              <div className="flex items-center gap-4">
                <Link
                  variant="primaryButton"
                  href={MainRoutes.Onboarding}
                  onClick={() => trackSignupClick('main')}
                >
                  See where you rank
                </Link>
                <div className="border-l border-topNav-divider h-[20px] w-1" />
                <span className="text-copy-accent text-sm max-w-[280px]">
                  It&apos;s free and only takes a minute to share your anonymous
                  salary
                </span>
              </div>
            ) : null}
            <SalaryPagination
              currentPage={pageNumber ?? 1}
              totalPages={pagesCount ?? 1}
              onPageChange={onPageChange}
            />
          </div>
        ) : (
          <InviteSalariesBanner isAuthenticated={isAuthenticated} />
        )}
      </div>
      <div className="block md:hidden">
        <SalaryTableMobile
          salaries={salaries}
          onRowClick={handleRowClick}
          blurTotalCompensation={hasReachedLimit}
          showTableRowHint={showTableRowHint}
          isLocumsOrPrn={isLocumsOrPrn}
          isHourlyPayPeriod={isHourlyPayPeriod}
        />
        {salaryMeta && !isSinglePage ? (
          <div className="mt-6">
            <SalaryPagination
              currentPage={pageNumber ?? 1}
              totalPages={pagesCount ?? 1}
              onPageChange={onPageChange}
            />
          </div>
        ) : (
          <InviteSalariesBanner isAuthenticated={isAuthenticated} />
        )}
      </div>
    </div>
  )
}
